import type { PaymentMethodBrand } from '../../../../types/registration-payment.types';
import { PAYMENT_METHOD_CONFIG } from '../constants/payment-method.const';

// Credit card icon for the empty state
const CardIcon = () => (
  <svg
    className="h-6 w-6 text-gray-400"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z"
    />
  </svg>
);

interface PaymentMethodCardProps {
  selectedBrand: PaymentMethodBrand | null;
  onAdd: () => void;
  onClear: () => void;
}

export function PaymentMethodCard({
  selectedBrand,
  onAdd,
  onClear
}: Readonly<PaymentMethodCardProps>) {
  const config = selectedBrand ? PAYMENT_METHOD_CONFIG[selectedBrand] : null;

  return (
    <div className="mt-6 w-full max-w-md">
      <div className="mb-2 flex items-center justify-start gap-x-2 px-1">
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-600">
          Payment Method
        </span>
        <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-500">
          Optional
        </span>
      </div>

      <div className="rounded-2xl border-2 border-gray-200 bg-white p-4 shadow-sm">
        {config ? (
          // Selected state
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src={config.logo}
                alt={config.label}
                className="h-10 w-14 rounded-md object-contain"
              />
              <span className="text-sm font-semibold text-gray-900">
                {config.label}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onAdd}
                className="text-sm font-medium text-[#5CB891] hover:text-[#4aa07c]">
                Change
              </button>
              <button
                type="button"
                onClick={onClear}
                className="text-sm font-medium text-gray-400 hover:text-red-400"
                aria-label="Remove payment method">
                ✕
              </button>
            </div>
          </div>
        ) : (
          // Empty state — dashed border clickable area
          <button
            type="button"
            onClick={onAdd}
            className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-300 py-4 text-sm text-gray-500 transition-colors hover:border-[#5CB891] hover:text-[#5CB891]">
            <div className="flex flex-col justify-center">
              <div className="flex flex-row items-center gap-x-2">
                <CardIcon />
                <span>Add a Payment Method</span>
              </div>
              <span className="text-xs">(Optional)</span>
            </div>
          </button>
        )}

        {!config && (
          <p className="mt-2 text-center text-xs text-gray-400">
            Link a card now for faster checkout later.
          </p>
        )}
      </div>
    </div>
  )
}
